import styled from 'styled-components/macro';
import SendIcon from '@mui/icons-material/Send';
import { Button, CircularProgress } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { translations } from '../../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { ChatRecord } from './ChatRecord';
import { w3cwebsocket as W3CWebSocket } from 'websocket';
import { useSelector } from 'react-redux';
import { selectUserEmail, selectUsername } from '../../../WorkListPage/slice/selectors';
import { selectContractNumber } from '../../slice/selectors';
import { toPathSegment } from '../../../../../utils/contractNumber';
import { v4 as uuid } from 'uuid';
import { chatRecord } from '../../slice/types';
import { UseAxios } from '../../../../../utils/UseAxios';
import { apiList } from '../../../../../locales/apiList';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { Divider, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { stringAvatar } from '../../../../../styles/StyleConstants';
import 'react-quill/dist/quill.snow.css';
import { CustomEditor } from './CustomEditor';
import { commonStyle } from '../../../../../styles/theme/themes';
import { getAccessToken } from '../../../../../utils/getAccessToken';


let URL = `${process.env.REACT_APP_CHATTER_WEB_SOCKET}`;
// const client = new W3CWebSocket(URL);
const reconnectingSocket = (url) => {
  getAccessToken().then(token => {
    URL = `${process.env.REACT_APP_CHATTER_WEB_SOCKET}${token}&env=${process.env.REACT_APP_ENVIRONMENT}`;
  });
  let client;
  let isConnected = false;
  let reconnectOnClose = true;
  let messageListeners: any[] = [];
  let stateChangeListeners: any[] = [];

  function on(fn) {
    messageListeners.push(fn);
    console.log('>>>on');
  }

  function off(fn) {
    messageListeners = messageListeners.filter(l => l !== fn);
    console.log('>>>off');
  }

  function onStateChange(fn) {
    stateChangeListeners.push(fn);
    return () => {
      stateChangeListeners = stateChangeListeners.filter(l => l !== fn);
    };
  }

  function start() {
    // client = new WebSocket(URL);
    client = new W3CWebSocket(URL);

    client.onopen = () => {
      isConnected = true;
      stateChangeListeners.forEach(fn => fn(true));
      console.log('>>>start');
    };

    const close = client.close;

    // Close without reconnecting;
    client.close = () => {
      reconnectOnClose = false;
      close.call(client);
      console.log('>>>close');
    };

    client.onmessage = (event) => {
      messageListeners.forEach(fn => fn(event.data));
    };

    client.onerror = (e) => console.error(e);

    client.onclose = () => {

      isConnected = false;
      stateChangeListeners.forEach(fn => fn(false));

      if (!reconnectOnClose) {
        console.log('ws closed by app');
        return;
      }

      console.log('ws closed by server');

      setTimeout(start, 3000);
    };
  }

  start();

  return {
    on,
    off,
    onStateChange,
    close: () => client.close(),
    getClient: () => client,
    isConnected: () => isConnected,
  };
};

const client = reconnectingSocket(URL);

function useMessage() {
  const [message, setMessage] = useState<any>();

  useEffect(() => {
    function handleMessage(message) {
      setMessage(message);
    }

    client.on(handleMessage);
    return () => client.off(handleMessage);
  }, [message, setMessage]);

  return message;
}

export function Chatter(props) {
  const { openChatter } = props;
  const { t } = useTranslation();
  const username = useSelector(selectUsername);
  const userEmail = useSelector(selectUserEmail);
  const contractNumber = useSelector(selectContractNumber);
  const [messageList, setMessageList] = useState<chatRecord[]>([]);
  const [files, setFiles] = useState<FileList>();
  const [selectedContentType, setSelectedContentType] = useState<string>('text');
  const [alignment, setAlignment] = React.useState<string | null>('left');
  const [loadingFile, setLoadingFile] = useState(false);
  const [mentionList, setMentionList] = useState<any[]>([]);
  const [emailMapping, setEmailMapping] = useState<any>({});
  const [replyMentionEmail, setReplyMentionEmail] = useState<string>();
  const [replyClick, setReplyClick] = useState<boolean>(false);
  const [resetInput, setResetInput] = useState<boolean>(false);
  const fileInput = useRef<any>();
  let userInput: string = '';

  const [isConnected, setIsConnected] = useState(client.isConnected());
  const message = useMessage();

  useEffect(() => {
    return client.onStateChange(setIsConnected);
  }, [setIsConnected]);

  useEffect(() => {
    if (isConnected) {
      client.getClient().send('hi');
    }
  }, [isConnected]);

  useEffect(() => {
    try {
      if (JSON.parse(JSON.parse(message)).contractNumber === contractNumber) {
        setMessageList(newMessageList => [JSON.parse(JSON.parse(message)), ...newMessageList]);
      }
      setLoadingFile(false);
    } catch (e) {
    }
  }, [message]);

  useEffect(() => {
    (async () => {
      let getBIResult = await UseAxios(`${apiList.getCHATRECORDSURL}${toPathSegment(contractNumber)}/chatRecords`, 'get');
      if (!getBIResult.error && getBIResult.data !== []) {
        const dataSortedByTime = getBIResult.data.sort(function(a, b) {
          return (new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());
        });
        setMessageList(dataSortedByTime);
      }
    })();
  }, [contractNumber]);

  useEffect(() => {
    (async () => {
      if (files) {
        let requestBody: any[] = [];
        for (const file of Array.from(files)) {
          const base64File = await convertBase64(file);
          if (typeof base64File === 'string') {
            const fileBase64 = base64File.split(',')[1];
            requestBody.push({
              file: fileBase64,
              type: file.type,
              fileName: file.name,
            });
          }
        }
        setLoadingFile(true);
        const uploadResult = await UseAxios(`${apiList.postAttachmentURL}${toPathSegment(contractNumber)}/attachment`, 'post', requestBody, false, false);
        if (!uploadResult.error) {
          const messageBody = {
            contractNumber: contractNumber,
            sortKey: uuid(),
            type: 'file',
            user: username,
            createdAt: new Date(),
            fileDatas: uploadResult.data,
          };
          client.getClient().send(JSON.stringify(messageBody));
          // if (client.getClient().readyState === 1) {
          //   client.getClient().send(JSON.stringify(messageBody));
          // }
        }
        setLoadingFile(false);
      }
    })();
  }, [files]);

  useEffect(() => {
    (async () => {
      const userLists = await UseAxios(`${apiList.geteBillWebBIUserList}`, 'get', null, true);
      if (userLists.data) {
        userLists.data.forEach(userData => {
          const usernameWithTitle = `${userData.last_name} ${userData.first_name} (${userData.title})`;
          const usernameData = {
            id: userData.id,
            value: usernameWithTitle,
          };
          setMentionList(mentionList => [...mentionList, usernameData]);
          emailMapping[usernameWithTitle] = userData.email;
        });
      }
    })();
  }, []);

  const convertBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null,
  ) => {
    setAlignment(newAlignment);
  };

  const handleSend = async () => {
    if (userInput.replace(/<(.|\n)*?>/g, '').trim().length > 0 || userInput.includes('<img')) {
      // setSocketConnected(false);
      const emailList = getMentionedEmails(userInput);
      const webbiUrl = `${window.location.href.split('detail')[0]}detail/${toPathSegment(contractNumber)}/openChatter`;

      const messageBody = {
        contractNumber: contractNumber,
        sortKey: uuid(),
        type: 'text',
        user: username,
        createdAt: new Date(),
        content: userInput,
        senderEmail: userEmail,
        mentionedEmail: emailList,
        webbiUrl: webbiUrl,
      };
      // if (client.readyState === 1) {
      //   client.send(JSON.stringify(messageBody));
      // }
      client.getClient().send(JSON.stringify(messageBody));
      setResetInput(true);
      setLoadingFile(true);
    }
  };

  const inputTextChange = (content) => {
    userInput = content;
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles(e.target.files);
    }
  };

  const getMentionedEmails = (userInput: string) => {
    const founds = userInput.match(/<span class="mention"(.|\n)*?>/g);
    let result: string[] = [];
    if (founds) {
      founds.forEach((found) => {
        const selectedName = found.split('data-value=')[1].replace(/["<>]*/g, '').trim();
        result.push(emailMapping[selectedName]);
      });
    }
    return result;
  };

  return (
    <Wrapper>
      <div className={`${openChatter ? 'display-chatter' : 'display-none'}`}>
        <div className='row'>
          <Avatar className='chatter-icon' {...stringAvatar(username, 30, 14)} />
          <div className='row-content'>
            {selectedContentType === 'text' &&
            <CustomEditor inputTextChange={inputTextChange} mentionList={mentionList} emailList={emailMapping}
                          replyMentionEmail={replyMentionEmail} replyClick={replyClick} setReplyClick={setReplyClick}
                          resetInput={resetInput} setResetInput={setResetInput} contractNumber={contractNumber} />
            }
            <div className={`send-row ${(selectedContentType === 'file') && 'send-file-row'}`}>
              <div>
                <ToggleButtonGroup
                  value={alignment}
                  exclusive
                  onChange={handleAlignment}
                  className='toggle-group'
                >
                  <ToggleButton value='left' onClick={() => setSelectedContentType('text')}>
                    Message
                  </ToggleButton>
                  <ToggleButton value='right' onClick={() => setSelectedContentType('file')}>
                    File
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
              {selectedContentType === 'text' ?
                <Button
                  className='send-btn'
                  variant='contained'
                  onClick={() => handleSend()}
                  endIcon={<SendIcon />}
                  disabled={!isConnected || loadingFile}
                >
                  {isConnected && !loadingFile ?
                    <div>{t(translations.chatter.send)}</div>
                    :
                    <CircularProgress size={'14px'} color={'inherit'} />
                  }
                </Button>
                :
                <div>
                  <Button
                    className='file-btn'
                    variant='contained'
                    endIcon={<AttachFileOutlinedIcon />}
                    component='label'
                    disabled={loadingFile}
                  >
                    {!loadingFile ?
                      <div>{t(translations.chatter.uploadFile)}</div>
                      :
                      <CircularProgress size={'14px'} color={'inherit'} />
                    }
                    <input type='file' hidden
                           ref={fileInput}
                           multiple
                           onChange={(event) => {
                             handleFileChange(event);
                           }}
                    />
                  </Button>
                </div>
              }
            </div>
            Max file size: 5MB.
          </div>
        </div>
        <Divider />
        <div className='record-list'>
          {messageList.map((item, index) => (
            <ChatRecord key={item.sortKey}
                        sender={item.user}
                        dateTime={item.createdAt}
                        content={item.content}
                        fileDatas={item.fileDatas}
                        senderEmail={item.senderEmail}
                        contentType={item.type}
                        setReplyMention={setReplyMentionEmail}
                        setReplyClick={setReplyClick}
            />
          ))}
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .display-none {
    display: none;
  }

  .display-chatter {
    margin: 5px 10px 0;
    padding: 5px 15px 5px 10px;
    background: ${p => p.theme.containerBackground};
    border-radius: 5px;
    box-shadow: ${commonStyle.boxShadow};
    position: fixed;
    bottom: 35px;
    right: 0;
    display: block;
    border: 1px solid ${p => p.theme.borderLight};
    max-width: 50em;
    min-width: 500px;
  }

  .row {
    width: 100%;
    display: flex;
    align-items: flex-start;
    margin-bottom: 5px;

    .row-content {
      width: 100%;
      display: block;
      margin-left: 5px;
      max-height: 200px;
      font-size: 12px;


      .send-file-row {
        padding-top: 5px;
      }
    }

    .chatter-icon {
      margin-top: 5px;
    }

    .input-field {
      width: 100%;
      margin: 0 0 0 5px;
    }
  }

  .record-list {
    margin-top: 5px;
    overflow-y: scroll;
    max-height: 350px;
    margin-right: -15px;
  }

  .send-row {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .toggle-group {
      height: 30px;
    }

    .send-btn {
      min-width: 90px;
      height: 30px;
      padding: 0;
    }

    .file-btn {
      min-width: 130px;
      height: 30px;
      padding: 0 5px;
    }

    .switch-btn {
      min-width: 90px;
      height: 35px;
      padding: 0;
    }
  }
`;
